/**
 * ConsentBanner Component
 *
 * Informational notice for the platform's strictly-necessary technical cookies.
 * These are GDPR/ePrivacy-exempt from consent (Art. 5(3) ePrivacy), so this is
 * an FYI banner, not a consent gate.
 *
 * Dismissal is persisted in a first-party functional cookie (itself strictly
 * necessary — it only remembers that the notice was shown) so the banner does
 * not reappear on every reload. This is the deliberate, approved exception to
 * the workspace "no browser storage" rule, scoped to this acknowledgment only —
 * no PII is stored.
 */

import { useState } from 'react';
import { Button } from '@/theme/components/ui/button';
import { useTranslation } from '@/core';

const CONSENT_COOKIE = 'shaka_cookie_notice_ack';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function hasAcknowledged(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie
    .split('; ')
    .some((entry) => entry.startsWith(`${CONSENT_COOKIE}=`));
}

function persistAcknowledged(): void {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${CONSENT_COOKIE}=1; Max-Age=${ONE_YEAR_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

export function ConsentBanner() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(() => !hasAcknowledged());

  const handleAccept = () => {
    persistAcknowledged();
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border shadow-lg">
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            {t('consent.message')}
          </p>
          <div className="flex gap-2 shrink-0">
            <a href="/terms" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">
                {t('consent.readTerms')}
              </Button>
            </a>
            <Button onClick={handleAccept} size="sm">
              {t('consent.accept')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
